import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Avatar,
  Button,
  Container,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import { useSelector } from 'react-redux';
import {
  scorecardErrorSelector,
  scorecardSavingSelector
} from '../../redux/features/applications/applicationsSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4)
  },
  paper: {
    padding: theme.spacing(4)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius
  },
  avatar: {
    width: 48,
    height: 48,
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.primary.main
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5)
  },
  textField: {
    marginBottom: theme.spacing(4)
  },
  toggleGroup: {
    display: 'flex',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  },
  toggleButton: {
    flex: 1,
    textTransform: 'none',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 0,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.grey[800],
    padding: theme.spacing(1.5, 1),
    '&:first-child': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius
    },
    '&:last-child': {
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      borderRight: `1px solid ${theme.palette.grey[300]}`
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  selectedVeryPoor: {
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.common.white
  },
  selectedPoor: {
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white
  },
  selectedGood: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  selectedVeryGood: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white
  },
  toggleLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
    fontSize: '0.875rem',
    justifyContent: 'center'
  },
  icon: {
    fontSize: '1rem'
  },
  actions: {
    marginTop: theme.spacing(4)
  },
  button: {
    padding: theme.spacing(1.5, 4)
  },
  alert: {
    marginTop: theme.spacing(2)
  }
}));

const CandidateScorecard = ({ candidate, initialValue, onSave }) => {
  const classes = useStyles();
  const [rating, setRating] = useState(initialValue?.rating || 'very_good');
  const [feedbackText, setFeedbackText] = useState(
    initialValue?.feedbackText || ''
  );
  const saving = useSelector(scorecardSavingSelector);
  const error = useSelector(scorecardErrorSelector);

  useEffect(() => {
    const nextRating = initialValue?.rating || 'very_good';
    setRating(nextRating);
  }, [initialValue.rating]);

  const handleRatingChange = (event, nextValue) => {
    if (nextValue) {
      setRating(nextValue);
    }
  };

  const handleSave = () => {
    onSave({ rating, feedbackText });
  };

  const headerName = candidate?.name || 'Candidate';
  const headerInitial = candidate?.name ? candidate.name.charAt(0) : '?';

  return (
    <Container maxWidth="md" className={classes.container}>
      <Paper elevation={2} className={classes.paper}>
        <div className={classes.header}>
          <Avatar className={classes.avatar} src={candidate?.avatarUrl || ''}>
            {headerInitial}
          </Avatar>
          <Typography variant="h6">{headerName}</Typography>
        </div>

        <Typography variant="h6" className={classes.sectionTitle}>
          Thoughts on this Candidate?
        </Typography>
        <TextField
          label="Your feedback"
          fullWidth
          multiline
          minRows={5}
          maxRows={5}
          variant="outlined"
          className={classes.textField}
          value={feedbackText}
          name="feedbackText"
          onChange={(event) => {
            const nextValue = event.target.value;
            setFeedbackText(nextValue);
          }}
        />

        <Typography variant="h6" className={classes.sectionTitle}>
          Overall Rating?
        </Typography>
        <ToggleButtonGroup
          value={rating}
          exclusive
          onChange={handleRatingChange}
          className={classes.toggleGroup}
          aria-label="Overall rating">
          <ToggleButton
            value="very_poor"
            classes={{
              root: classes.toggleButton,
              selected: classes.selectedVeryPoor
            }}
            aria-label="Very poor">
            <span className={classes.toggleLabel}>
              <ThumbDownAltOutlinedIcon className={classes.icon} />
              <ThumbDownAltOutlinedIcon className={classes.icon} />
              Very Poor
            </span>
          </ToggleButton>
          <ToggleButton
            value="poor"
            classes={{
              root: classes.toggleButton,
              selected: classes.selectedPoor
            }}
            aria-label="Poor">
            <span className={classes.toggleLabel}>
              <ThumbDownAltOutlinedIcon className={classes.icon} />
              Poor
            </span>
          </ToggleButton>
          <ToggleButton
            value="good"
            classes={{
              root: classes.toggleButton,
              selected: classes.selectedGood
            }}
            aria-label="Good">
            <span className={classes.toggleLabel}>
              <ThumbUpAltOutlinedIcon className={classes.icon} />
              Good
            </span>
          </ToggleButton>
          <ToggleButton
            value="very_good"
            classes={{
              root: classes.toggleButton,
              selected: classes.selectedVeryGood
            }}
            aria-label="Very good">
            <span className={classes.toggleLabel}>
              <ThumbUpAltOutlinedIcon className={classes.icon} />
              <ThumbUpAltOutlinedIcon className={classes.icon} />
              Very Good
            </span>
          </ToggleButton>
        </ToggleButtonGroup>

        <div className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            className={classes.button}>
            {saving ? 'Saving...' : 'Save Scorecard'}
          </Button>
        </div>

        {error && (
          <Alert severity="error" className={classes.alert}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

CandidateScorecard.propTypes = {
  candidate: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    avatarUrl: PropTypes.string
  }),
  initialValue: PropTypes.shape({
    feedbackText: PropTypes.string,
    rating: PropTypes.string
  }),
  onSave: PropTypes.func.isRequired
};

CandidateScorecard.defaultProps = {
  candidate: null,
  initialValue: null
};

export default CandidateScorecard;
